import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getEmail, analyzeEmail, generateDraft, sendChatMessage, uploadFile, analyzeEmailAttachments, downloadEmailLinks, resyncEmailAttachments } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { formatDate, getStatusColor } from '../../lib/utils';
import { toast } from 'sonner';
import { ArrowLeft, Paperclip, Bot, Send, Sparkles, Loader2, FileText, MessageSquare, Check, X, Upload, RefreshCw, Download, Link } from 'lucide-react';

function EmailDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [draft, setDraft] = useState(null);
  const [genDraft, setGenDraft] = useState(false);
  const [tab, setTab] = useState('content');
  const [msgs, setMsgs] = useState([]);
  const [chatIn, setChatIn] = useState('');
  const [chatLoad, setChatLoad] = useState(false);
  const [sid, setSid] = useState(null);
  
  // File attachment states
  const [attachedFile, setAttachedFile] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [analyzingAttachments, setAnalyzingAttachments] = useState(false);
  const [downloadingLinks, setDownloadingLinks] = useState(false);
  const [resyncingAttachments, setResyncingAttachments] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(function() { fetchEmail(); }, [id]);

  async function fetchEmail() {
    try {
      const res = await getEmail(id);
      setEmail(res.data);
      if (res.data.ai_summary) {
        setAnalysis({ summary_en: res.data.ai_summary, summary_ar: res.data.ai_summary_ar, suggestions: res.data.ai_suggestions, is_quote_request: res.data.is_quote_request });
      }
    } catch (e) { toast.error('فشل'); }
    finally { setLoading(false); }
  }

  async function doAnalyzeAttachments() {
    setAnalyzingAttachments(true);
    try {
      const r = await analyzeEmailAttachments(id);
      toast.success(r.data.message_ar || 'تم تحليل المرفقات');
      // Refresh email to get updated attachments
      await fetchEmail();
    } catch (e) {
      toast.error('فشل تحليل المرفقات');
    } finally {
      setAnalyzingAttachments(false);
    }
  }

  async function doDownloadLinks() {
    setDownloadingLinks(true);
    try {
      const r = await downloadEmailLinks(id);
      if (r.data.downloaded > 0) {
        toast.success(r.data.message_ar || `تم تنزيل ${r.data.downloaded} ملفات من الروابط`);
        // Refresh email to get new attachments
        await fetchEmail();
      } else {
        toast.info('لم يتم العثور على روابط ملفات قابلة للتنزيل');
      }
    } catch (e) {
      toast.error('فشل تنزيل الملفات من الروابط');
    } finally {
      setDownloadingLinks(false);
    }
  }

  async function doResyncAttachments() {
    setResyncingAttachments(true);
    try {
      const r = await resyncEmailAttachments(id);
      if (r.data.success) {
        toast.success(r.data.message || 'تم مزامنة المرفقات بنجاح');
        // Refresh email to get updated attachments
        await fetchEmail();
      } else {
        toast.warning(r.data.message || 'لم يتم العثور على مرفقات جديدة');
      }
    } catch (e) {
      toast.error('فشل مزامنة المرفقات من الخادم');
    } finally {
      setResyncingAttachments(false);
    }
  }

  async function doAnalyze() {
    setAnalyzing(true);
    try { const r = await analyzeEmail(id); setAnalysis(r.data); toast.success('تم'); }
    catch (e) { toast.error('فشل'); }
    finally { setAnalyzing(false); }
  }

  async function doDraft(t) {
    setGenDraft(true);
    try { const r = await generateDraft(id, t, ''); setDraft(r.data); toast.success('تم'); }
    catch (e) { toast.error('فشل'); }
    finally { setGenDraft(false); }
  }

  async function handleFileSelect(e) {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error('حجم الملف كبير جداً. الحد الأقصى 10MB');
      return;
    }

    setUploadingFile(true);
    try {
      const response = await uploadFile(file, id);
      const analysisText = response.data.analysis?.text || response.data.analysis?.error || 'تم رفع الملف بنجاح';
      setAttachedFile({
        name: file.name,
        type: file.type,
        size: file.size,
        content: analysisText
      });
      toast.success('تم رفع الملف بنجاح');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('فشل رفع الملف');
    } finally {
      setUploadingFile(false);
    }
  }

  function removeAttachedFile() {
    setAttachedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }

  async function doChat() {
    if (!chatIn.trim() && !attachedFile) return;
    
    const userMessage = chatIn || 'حلل هذا الملف';
    const displayMessage = attachedFile ? userMessage + '\n📎 ' + attachedFile.name : userMessage;
    
    setChatIn('');
    setMsgs(function(p) { return [...p, { role: 'user', content: displayMessage, file: attachedFile }]; });
    setChatLoad(true);
    
    try {
      const fileContent = attachedFile ? attachedFile.content : null;
      const fileName = attachedFile ? attachedFile.name : null;
      
      const r = await sendChatMessage(userMessage, id, sid, fileContent, fileName);
      setSid(r.data.session_id);
      setMsgs(function(p) { return [...p, { role: 'assistant', content: r.data.response }]; });
    } catch (e) {
      toast.error('فشل');
    } finally {
      setChatLoad(false);
      removeAttachedFile();
    }
  }

  function getFileIcon(type) {
    if (!type) return '📎';
    if (type.includes('pdf')) return '📄';
    if (type.includes('word')) return '📝';
    if (type.includes('excel') || type.includes('sheet')) return '📊';
    if (type.includes('image')) return '🖼️';
    return '📎';
  }

  if (loading) { return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>; }
  if (!email) { return <div className="text-center py-12"><p className="text-muted-foreground">غير موجود</p></div>; }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={function() { navigate('/emails'); }} data-testid="back-btn"><ArrowLeft className="h-5 w-5" /></Button>
        <div className="flex-1">
          <h1 className="font-heading text-2xl font-bold uppercase">{email.subject}</h1>
          <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
            <span>من: {email.sender}</span>
            <span className="font-mono">{formatDate(email.created_at)}</span>
            <Badge variant="outline" className={getStatusColor(email.status)}>{email.status}</Badge>
          </div>
        </div>
      </div>

      <div className="flex gap-2 border-b border-border pb-2">
        <Button variant={tab === 'content' ? 'default' : 'ghost'} size="sm" onClick={function() { setTab('content'); }} data-testid="tab-content">المحتوى</Button>
        <Button variant={tab === 'analysis' ? 'default' : 'ghost'} size="sm" onClick={function() { setTab('analysis'); }} data-testid="tab-analysis">التحليل</Button>
        <Button variant={tab === 'chat' ? 'default' : 'ghost'} size="sm" onClick={function() { setTab('chat'); }} data-testid="tab-chat">المساعد</Button>
        <Button variant={tab === 'draft' ? 'default' : 'ghost'} size="sm" onClick={function() { setTab('draft'); }} data-testid="tab-draft">الرد</Button>
      </div>

      {tab === 'content' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2 bg-card border-border">
            <CardHeader><CardTitle>محتوى الرسالة</CardTitle></CardHeader>
            <CardContent><pre className="whitespace-pre-wrap text-sm bg-muted/30 p-4 rounded-sm">{email.body}</pre></CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardHeader>
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2"><Paperclip className="h-5 w-5" />المرفقات</CardTitle>
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={doResyncAttachments} 
                      disabled={resyncingAttachments}
                      title="إعادة مزامنة المرفقات من خادم البريد"
                      data-testid="resync-attachments-btn"
                    >
                      {resyncingAttachments ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={doDownloadLinks} 
                      disabled={downloadingLinks}
                      title="تنزيل الملفات من الروابط في الإيميل"
                      data-testid="download-links-btn"
                    >
                      {downloadingLinks ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                    </Button>
                    {email.attachments && email.attachments.length > 0 && email.attachments.some(a => !a.analysis) && (
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={doAnalyzeAttachments} 
                        disabled={analyzingAttachments}
                        data-testid="analyze-attachments-btn"
                      >
                        {analyzingAttachments ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                        {analyzingAttachments ? 'جاري...' : 'تحليل'}
                      </Button>
                    )}
                  </div>
                </div>
                {downloadingLinks && (
                  <p className="text-xs text-muted-foreground">جاري البحث عن روابط وتنزيل الملفات...</p>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {email.attachments && email.attachments.length > 0 ? (
                <div className="space-y-3">
                  {email.attachments.map(function(a, i) { 
                    return (
                      <div key={i} className="p-3 rounded-sm bg-muted/30 border space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium flex items-center gap-2">
                            {a.source_url ? <Link className="h-4 w-4 text-blue-500" /> : <FileText className="h-4 w-4" />}
                            {a.filename}
                          </p>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">{a.file_type}</Badge>
                            {a.url && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => window.open(a.url, '_blank')}
                                data-testid={`view-attachment-${i}`}
                              >
                                <Download className="h-3 w-3 mr-1" />
                                عرض
                              </Button>
                            )}
                          </div>
                        </div>
                        {a.source_url && (
                          <p className="text-xs text-blue-500 truncate">🔗 تم تنزيله من رابط</p>
                        )}
                        {a.analysis ? (
                          <details className="group">
                            <summary className="cursor-pointer p-2 bg-primary/5 border border-primary/20 rounded text-xs font-medium text-primary flex items-center justify-between">
                              <span>📋 محتوى الملف</span>
                              <span className="text-muted-foreground group-open:hidden">اضغط للعرض</span>
                              <span className="text-muted-foreground hidden group-open:inline">اضغط للإخفاء</span>
                            </summary>
                            <div className="p-2 bg-primary/5 border border-t-0 border-primary/20 rounded-b">
                              <pre className="whitespace-pre-wrap text-muted-foreground max-h-48 overflow-y-auto text-xs">{a.analysis}</pre>
                            </div>
                          </details>
                        ) : (
                          <div className="p-2 bg-yellow-500/10 border border-yellow-500/20 rounded text-xs text-yellow-600">
                            ⏳ لم يتم تحليل الملف بعد - اضغط "تحليل"
                          </div>
                        )}
                      </div>
                    ); 
                  })}
                </div>
              ) : <p className="text-sm text-muted-foreground text-center py-4">لا مرفقات</p>}
            </CardContent>
          </Card>
        </div>
      )}

      {tab === 'analysis' && (
        <Card className="bg-card border-border">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-primary" />التحليل</CardTitle>
            <Button onClick={doAnalyze} disabled={analyzing} data-testid="analyze-btn">{analyzing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Bot className="h-4 w-4 mr-2" />}تحليل</Button>
          </CardHeader>
          <CardContent>
            {analysis ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-muted/30 rounded-sm border"><h3 className="font-medium mb-2">Summary</h3><p className="text-sm text-muted-foreground">{analysis.summary_en}</p></div>
                  <div className="p-4 bg-muted/30 rounded-sm border" dir="rtl"><h3 className="font-medium mb-2">الملخص</h3><p className="text-sm text-muted-foreground">{analysis.summary_ar}</p></div>
                </div>
                {analysis.is_quote_request && <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-sm"><p className="text-green-500 flex items-center gap-2"><Check className="h-5 w-5" />طلب عرض سعر</p></div>}
              </div>
            ) : <div className="text-center py-12 text-muted-foreground"><Bot className="h-16 w-16 mx-auto mb-4 opacity-50" /><p>اضغط تحليل</p></div>}
          </CardContent>
        </Card>
      )}

      {tab === 'chat' && (
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-primary" />
              المساعد
              {email.attachments && email.attachments.length > 0 && (
                <Badge variant="secondary" className="mr-2">
                  📎 {email.attachments.length} مرفقات متاحة
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72 overflow-y-auto space-y-4 mb-4">
              {msgs.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Bot className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p className="mb-2">ابدأ المحادثة مع Nicole</p>
                  {email.attachments && email.attachments.length > 0 && (
                    <p className="text-xs text-primary">Nicole لديها إمكانية الوصول لمرفقات هذا الإيميل تلقائياً</p>
                  )}
                </div>
              ) : msgs.map(function(m, i) {
                return (
                  <div key={i} className={m.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
                    <div className={m.role === 'user' ? 'max-w-[80%] p-3 rounded-sm bg-primary text-primary-foreground' : 'max-w-[80%] p-3 rounded-sm bg-muted/50 border'}>
                      {m.file && (
                        <div className="flex items-center gap-2 mb-2 pb-2 border-b border-current/20">
                          <span>{getFileIcon(m.file.type)}</span>
                          <span className="text-sm font-medium">{m.file.name}</span>
                        </div>
                      )}
                      <pre className="whitespace-pre-wrap text-sm">{m.content}</pre>
                    </div>
                  </div>
                );
              })}
              {chatLoad && <div className="flex justify-start"><div className="bg-muted/50 border p-3 rounded-sm"><Loader2 className="h-5 w-5 animate-spin" /></div></div>}
            </div>
            
            {/* Attached File Preview */}
            {attachedFile && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-green-500/10 border border-green-500/20 mb-3">
                <FileText className="h-5 w-5 text-green-500" />
                <span className="flex-1 text-sm font-medium">{attachedFile.name}</span>
                <span className="text-xs text-muted-foreground">{Math.round(attachedFile.size / 1024)} KB</span>
                <span className="text-xs text-green-500">✓ جاهز للتحليل</span>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    toast.info('الملف متاح محلياً للتحليل');
                  }}
                  data-testid="view-attached-email-btn"
                >
                  View Attached
                </Button>
                <Button variant="ghost" size="icon" onClick={removeAttachedFile} className="h-6 w-6">
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}
            
            {/* Input Area with File Upload */}
            <div className="flex gap-2">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept=".pdf,.docx,.xlsx,.txt,.png,.jpg,.jpeg"
                className="hidden"
                data-testid="file-input-chat"
              />
              <Button
                variant="outline"
                size="icon"
                onClick={function() { fileInputRef.current.click(); }}
                disabled={uploadingFile}
                className="flex-shrink-0"
                title="إرفاق ملف PDF, Word, Excel"
                data-testid="attach-file-chat-btn"
              >
                {uploadingFile ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              </Button>
              <Input 
                value={chatIn} 
                onChange={function(e) { setChatIn(e.target.value); }} 
                placeholder={attachedFile ? "اسأل عن الملف المرفق..." : "رسالتك..."} 
                onKeyPress={function(e) { if (e.key === 'Enter') doChat(); }} 
                data-testid="chat-input" 
              />
              <Button onClick={doChat} disabled={chatLoad || (!chatIn.trim() && !attachedFile)} data-testid="send-chat-btn">
                <Send className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground text-center mt-2">
              📎 يمكنك إرفاق ملف PDF, Word, Excel لتحليله
            </p>
          </CardContent>
        </Card>
      )}

      {tab === 'draft' && (
        <Card className="bg-card border-border">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>إنشاء رد</CardTitle>
            <div className="flex gap-2">
              <Button onClick={function() { doDraft('acknowledgment'); }} disabled={genDraft} variant="outline" data-testid="generate-ack-btn">إشعار</Button>
              <Button onClick={function() { doDraft('general'); }} disabled={genDraft} data-testid="generate-general-btn">{genDraft ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}رد</Button>
            </div>
          </CardHeader>
          <CardContent>
            {draft ? (
              <div className="space-y-4">
                <Input value={draft.subject} readOnly className="bg-input/50" />
                <div className="grid grid-cols-2 gap-4">
                  <Textarea value={draft.body_en} readOnly rows={6} className="bg-input/50" />
                  <Textarea value={draft.body_ar} readOnly rows={6} className="bg-input/50" dir="rtl" />
                </div>
              </div>
            ) : <div className="text-center py-12 text-muted-foreground"><FileText className="h-16 w-16 mx-auto mb-4 opacity-50" /><p>اختر نوع الرد</p></div>}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default EmailDetailPage;
